/**
 * Página de gestión de compras
 * 
 * Muestra el listado de compras a proveedores y permite
 * filtrar, ver detalles y cambiar estados.
 * 
 * @module pages/compras/Compras
 * @requires react, react-router-dom, ../../services/compras.service
 * @related_files ./CompraForm.js, ./DetalleCompra.js
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Servicios
import comprasService from '../../services/compras.service';
import { useAuth } from '../../contexts/AuthContext';
import { matrixAllows } from '../../utils/matrixAllows';
import {
  getCompraEstadoLabel,
  isCompraPendiente,
  isCompraRecibida,
  warnDetallesSinProductoId
} from '../../utils/compraEstado';

// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Table from '../../components/common/Table';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CompraCostosCambioModal from '../../components/compras/CompraCostosCambioModal';

// Iconos
import { 
  FaShoppingCart, FaPlus, FaEye, FaCheck, FaTimes,
  FaClock, FaCalendarAlt, FaUserTie, FaTruck, FaBalanceScale
} from 'react-icons/fa';

/**
 * Componente de página para gestión de compras
 * @returns {JSX.Element} Componente Compras
 */
const Compras = () => {
  const navigate = useNavigate();
  const { currentUser, permisosEfectivos } = useAuth();

  // Estado
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [compraProcesando, setCompraProcesando] = useState(null);
  const [accion, setAccion] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [costosModal, setCostosModal] = useState({
    open: false,
    compraId: null,
    cambios: [],
    applying: false
  });
  
  /**
   * Carga inicial de datos
   */
  useEffect(() => {
    cargarCompras();
  }, []);
  
  /**
   * Carga todas las compras
   */
  const cargarCompras = async () => {
    const LOAD_TIMEOUT_MS = 20000;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      setLoadError('La carga de compras tardó demasiado. Revisá conexión o permisos (compras.ver / alcance).');
      setLoading(false);
      toast.error('Tiempo de espera agotado al cargar compras');
    }, LOAD_TIMEOUT_MS);

    try {
      setLoading(true);
      setLoadError(null);
      const data = await comprasService.obtenerTodas();
      if (timedOut) return;
      setCompras(Array.isArray(data) ? data : []);
    } catch (error) {
      if (timedOut) return;
      console.error('Error al cargar compras:', error);
      const status = error?.response?.status || error?.status;
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Error al cargar las compras';
      setLoadError(
        status === 403
          ? 'No tenés permiso para ver compras.'
          : (msg || 'No pudimos cargar las compras. Reintentá.')
      );
      setCompras([]);
      toast.error(status === 403 ? 'No tenés permiso para ver compras.' : (msg || 'No pudimos cargar las compras.'));
    } finally {
      clearTimeout(timer);
      if (!timedOut) setLoading(false);
    }
  };
  
  /**
   * Formatea el total con 2 decimales y el símbolo de moneda
   * @param {number} total - Valor a formatear
   * @returns {string} Valor formateado
   */
  const formatearTotal = (total) => {
    return `$${parseFloat(total).toFixed(2)}`;
  };
  
  /**
   * Prepara la recepción de una compra
   * @param {Object} compra - Compra a recibir
   */
  const prepararRecibirCompra = (compra) => {
    setCompraProcesando(compra);
    setAccion('recibir');
    setShowConfirmDialog(true);
  };
  
  /**
   * Prepara la cancelación de una compra
   * @param {Object} compra - Compra a cancelar
   */
  const prepararCancelarCompra = (compra) => {
    setCompraProcesando(compra);
    setAccion('cancelar');
    setShowConfirmDialog(true);
  };
  
  const aplicarCostosSeleccionLista = async (producto_ids) => {
    const cid = costosModal.compraId;
    if (!cid) return;
    setCostosModal((s) => ({ ...s, applying: true }));
    try {
      await comprasService.aplicarCostosProductos(cid, producto_ids);
      toast.success(`Costo actualizado en ${producto_ids.length} producto(s).`, {
        autoClose: 3600,
        className: '!bg-gradient-to-r !from-emerald-600 !to-teal-600 !text-white'
      });
      setCostosModal({ open: false, compraId: null, cambios: [], applying: false });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'No se pudieron actualizar los costos');
    } finally {
      setCostosModal((s) => ({ ...s, applying: false }));
    }
  };

  /**
   * Confirma la acción sobre la compra
   */
  const confirmarAccion = async () => {
    try {
      if (accion === 'recibir') {
        const compraIdRecibir = compraProcesando.id;
        warnDetallesSinProductoId(compraProcesando.detalles, compraIdRecibir);
        const data = await comprasService.recibirCompra(compraIdRecibir, {
          usuario_id: currentUser?.id || currentUser?.email || '',
          detalles: compraProcesando.detalles
        });
        const estadoResp = data?.estado ?? data?.data?.estado;
        const procesados = data?.productos_procesados ?? data?.data?.productos_procesados;
        const resolved = data?.cambios_costo != null ? data : data?.data || {};
        const cambios = Array.isArray(resolved.cambios_costo) ? resolved.cambios_costo : [];
        if (estadoResp && estadoResp !== 'recibida') {
          toast.warning('Recepción registrada, pero el servidor no confirmó estado "recibida".');
        } else {
          toast.success(
            cambios.length > 0
              ? 'Compra recibida. Stock listo — podés alinear costos en ficha.'
              : procesados != null
                ? `Compra recibida. Stock actualizado (${procesados} producto(s)).`
                : 'Compra recibida. Stock actualizado.',
            { autoClose: 4000 }
          );
        }
        if (cambios.length > 0) {
          setCostosModal({
            open: true,
            compraId: compraIdRecibir,
            cambios,
            applying: false
          });
        }
      } else if (accion === 'cancelar') {
        await comprasService.cancelarCompra(compraProcesando.id);
        toast.success('Compra cancelada correctamente');
      }
      
      // Recargar compras
      cargarCompras();
    } catch (error) {
      console.error(`Error al ${accion} compra:`, error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Error al ${accion} la compra`);
      }
    } finally {
      setShowConfirmDialog(false);
      setCompraProcesando(null);
      setAccion('');
    }
  };
  
  /**
   * Cancela la acción sobre la compra
   */
  const cancelarAccion = () => {
    setShowConfirmDialog(false);
    setCompraProcesando(null);
    setAccion('');
  };
  
  /**
   * Navega a los detalles de la compra
   * @param {number} id - ID de la compra
   */
  const verDetalleCompra = (id) => {
    navigate(`/compras/${id}`);
  };
  
  /**
   * Columnas para la tabla de compras
   */
  const columns = [
    {
      header: 'Código',
      accessor: 'numero',
      cell: (row) => (
        <span className="font-medium">{row.numero}</span>
      )
    },
    {
      header: 'Fecha',
      accessor: 'fecha',
      cell: (row) => (
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2 text-gray-500" />
          <span>{new Date(row.fecha).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      header: 'Proveedor',
      accessor: 'proveedor',
      cell: (row) => (
        <div className="flex items-center">
          <FaUserTie className="mr-2 text-gray-500" />
          <span>{row.proveedor}</span>
        </div>
      )
    },
    {
      header: 'Estado',
      accessor: 'estado',
      cell: (row) => {
        let color = 'bg-gray-100 text-gray-600';
        let icon = <FaClock className="mr-1" />;
        
        if (isCompraRecibida(row.estado)) {
          color = 'bg-green-100 text-green-600';
          icon = <FaCheck className="mr-1" />;
        } else if (row.estado === 'cancelada') {
          color = 'bg-red-100 text-red-600';
          icon = <FaTimes className="mr-1" />;
        }
        
        return (
          <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center ${color}`}>
            {icon}
            {getCompraEstadoLabel(row.estado)}
          </span>
        );
      }
    },
    {
      header: 'Total',
      accessor: 'total',
      cell: (row) => (
        <span className="font-medium">
          {formatearTotal(row.total)}
        </span>
      )
    },
    {
      header: 'Acciones',
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => verDetalleCompra(row.id)}
            className="text-blue-600 hover:text-blue-800"
            title="Ver detalles"
          >
            <FaEye />
          </button>
          
          {isCompraPendiente(row.estado) && (
            <>
              <button
                onClick={() => prepararRecibirCompra(row)}
                className="text-green-600 hover:text-green-800"
                title="Recibir compra"
              >
                <FaCheck />
              </button>
              
              <button
                onClick={() => prepararCancelarCompra(row)}
                className="text-red-600 hover:text-red-800"
                title="Cancelar compra"
              >
                <FaTimes />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Gestión de Compras</h1>
        
        <div className="flex flex-wrap items-center gap-2">
          {matrixAllows(permisosEfectivos, 'productos', 'ver') && (
            <Link to="/productos/comparador-precios">
              <Button color="secondary" icon={<FaBalanceScale />}>Comparar precios</Button>
            </Link>
          )}
          <Link to="/proveedores">
            <Button color="secondary" icon={<FaTruck />}>
              Ver proveedores
            </Button>
          </Link>
          <Link to="/compras/nueva">
            <Button color="primary" icon={<FaPlus />}>
              Nueva compra
            </Button>
          </Link>
        </div>
      </div>
      
      <Card>
        <div className="flex justify-between space-x-4">
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              Compras a proveedores
            </h3>
            <p className="text-gray-600">
              Gestiona las órdenes de compra a proveedores y su recepción en el inventario.
            </p>
          </div>
          
          <div className="flex items-center">
            <div className="text-sm text-gray-500 flex flex-col space-y-2">
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-gray-100 mr-2"></span>
                Pendiente
              </div>
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-green-100 mr-2"></span>
                Recibida
              </div>
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-red-100 mr-2"></span>
                Cancelada
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      <Card>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-e0e3-muted">Cargando compras…</p>
            <Button color="secondary" size="sm" onClick={cargarCompras}>
              Cancelar / reintentar
            </Button>
          </div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-200 p-8 rounded-lg text-center">
            <FaTimes className="mx-auto text-4xl text-red-400 mb-2" />
            <h3 className="text-lg font-medium text-red-800 mb-1">No se pudo cargar compras</h3>
            <p className="text-red-700 text-sm mb-4">{loadError}</p>
            <Button color="primary" onClick={cargarCompras}>
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            {compras.length === 0 ? (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <FaShoppingCart className="mx-auto text-4xl text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">
                  No hay compras registradas.
                </h3>
                <p className="text-gray-500">
                  Antes de comprar, asegurate de tener un proveedor. Luego registrá la orden.
                </p>
                
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <Link to="/proveedores">
                    <Button color="secondary" icon={<FaTruck />}>
                      Gestionar proveedores
                    </Button>
                  </Link>
                  <Link to="/compras/nueva">
                    <Button color="primary" icon={<FaPlus />}>
                      Registrar compra
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <Table
                columns={columns}
                data={compras}
                pagination={true}
                itemsPerPage={10}
              />
            )}
          </>
        )}
      </Card>
      
      {/* Diálogo de confirmación */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={accion === 'recibir' ? 'Recibir Compra' : 'Cancelar Compra'}
        message={
          compraProcesando
            ? accion === 'recibir'
              ? `¿Estás seguro de marcar la compra ${compraProcesando.numero} como recibida? Esta acción actualizará el inventario.`
              : `¿Estás seguro de cancelar la compra ${compraProcesando.numero}? Esta acción no se puede deshacer.`
            : ''
        }
        confirmText={accion === 'recibir' ? 'Recibir' : 'Cancelar'}
        cancelText="Volver"
        onConfirm={confirmarAccion}
        onCancel={cancelarAccion}
      />

      <CompraCostosCambioModal
        open={costosModal.open}
        cambios={costosModal.cambios}
        applying={costosModal.applying}
        onClose={() => setCostosModal({ open: false, compraId: null, cambios: [], applying: false })}
        onApply={aplicarCostosSeleccionLista}
      />
    </div>
  );
};

export default Compras;
